/**
 * Legal document content.
 *
 * These texts describe how the platform actually behaves — refund windows, data
 * processors and retention are taken from the backend implementation, not from a
 * generic template. If you change that behaviour, change these documents too.
 *
 * Language policy: the documents are maintained in English and Spanish. Visitors
 * browsing in another locale are served the English text with a notice, because a
 * machine-translated legal document is worse than an honest pointer to the
 * authoritative one.
 *
 * Operator identification lives in ./operator.ts, deliberately separate: the footer
 * needs those few fields on every page, and importing them from here dragged the whole
 * legal text into every route's bundle.
 */

import { OPERATOR, operatorName } from "@/lib/operator";

export { OPERATOR, operatorName, operatorDetailsHidden } from "@/lib/operator";
export type { OperatorDetails } from "@/lib/operator";

const CONTACT_FORM = {
  en: "the contact form on this site",
  es: "el formulario de contacto de este sitio",
} as const;

/** Where to send a support request: the published inbox, or the contact form. */
function supportChannel(lang: LegalLang): string {
  return OPERATOR.supportEmail ?? CONTACT_FORM[lang];
}

/** Where to send a data-protection request. */
function privacyChannel(lang: LegalLang): string {
  return OPERATOR.privacyEmail ?? OPERATOR.supportEmail ?? CONTACT_FORM[lang];
}

/**
 * "Legal Name, Address. Registration number X. Contact: Y." — omitted entirely while
 * the details are hidden, rather than printed with gaps.
 */
function identificationLine(lang: LegalLang): string | null {
  if (!OPERATOR.legalName) return null;
  const parts = [
    OPERATOR.address ? `${OPERATOR.legalName}, ${OPERATOR.address}.` : `${OPERATOR.legalName}.`,
  ];
  if (OPERATOR.registrationNumber) {
    parts.push(
      lang === "es"
        ? `Número de registro ${OPERATOR.registrationNumber}.`
        : `Registration number ${OPERATOR.registrationNumber}.`,
    );
  }
  parts.push(
    lang === "es" ? `Contacto: ${supportChannel(lang)}.` : `Contact: ${supportChannel(lang)}.`,
  );
  return parts.join(" ");
}

/** Governing-law sentence — only meaningful once the country/jurisdiction are set. */
function governingLawLine(lang: LegalLang): string | null {
  if (!OPERATOR.country || !OPERATOR.jurisdiction) return null;
  return lang === "es"
    ? `Estos términos se rigen por la legislación de ${OPERATOR.country} y son competentes los tribunales de ${OPERATOR.jurisdiction}, sin perjuicio de la protección imperativa que te corresponda en tu país de residencia.`
    : `These terms are governed by the law of ${OPERATOR.country}, and the courts of ${OPERATOR.jurisdiction} have jurisdiction, without affecting any mandatory protection available to you in your country of residence.`;
}

/** Drop the nulls left by an omitted clause. */
function lines(...candidates: (string | null)[]): string[] {
  return candidates.filter((line): line is string => Boolean(line));
}

export const LEGAL_LANGS = ["en", "es"] as const;
export type LegalLang = (typeof LEGAL_LANGS)[number];

export const LEGAL_SLUGS = ["terms", "privacy", "refund", "cookies"] as const;
export type LegalSlug = (typeof LEGAL_SLUGS)[number];

export interface LegalSection {
  heading: string;
  /** Paragraphs. */
  body?: string[];
  /** Bullet list rendered after the paragraphs. */
  list?: string[];
}

export interface LegalDoc {
  title: string;
  metaTitle: string;
  metaDescription: string;
  /** ISO date of the last substantive edit. */
  updated: string;
  intro: string;
  sections: LegalSection[];
}

/** Resolve a requested locale to the language the documents exist in. */
export function resolveLegalLang(locale: string): LegalLang {
  return (LEGAL_LANGS as readonly string[]).includes(locale) ? (locale as LegalLang) : "en";
}

const LAST_UPDATED = "2026-08-12";

// ─────────────────────────────────────────────────────────────────────────────
// English
// ─────────────────────────────────────────────────────────────────────────────

const EN: Record<LegalSlug, LegalDoc> = {
  terms: {
    title: "Terms of Service",
    metaTitle: "Terms of Service — SmsEmpire",
    metaDescription:
      "The terms governing your use of SmsEmpire: what the service does, what you may and may not use it for, how prepaid balance works, and how the agreement ends.",
    updated: LAST_UPDATED,
    intro: `These terms form the agreement between you and ${operatorName()} ("we", "us")${OPERATOR.legalName ? `, operator of ${OPERATOR.tradingName}` : ""}. By creating an account you accept them. If you do not accept them, do not use the service.`,
    sections: [
      {
        heading: "1. What the service is",
        body: [
          `${OPERATOR.tradingName} is a direct provider. We provide access to virtual phone numbers and disposable email inboxes, and we resell pre-made accounts. We do not own the telephone numbering ranges and we do not operate the mobile networks that deliver the messages.`,
          "This matters for what we can promise: we can pass on a message that reaches us, but we cannot force a third party to send one. Availability, delivery times and per-country stock are determined by our network capacity and by the platform you are verifying against.",
        ],
      },
      {
        heading: "2. Eligibility",
        body: [
          "You must be at least 18 years old and legally able to enter into a contract. One person may hold one account. You are responsible for everything done through your account, so keep your password to yourself.",
          "We may refuse or close an account where we reasonably believe these terms are being breached.",
        ],
      },
      {
        heading: "3. Prepaid balance",
        body: [
          "The service runs on prepaid balance. You add funds, and each activation deducts its price at the moment you request it. Prices are quoted in US dollars; any other currency shown in the interface is an indicative conversion, and the amount actually charged by the payment processor is the US dollar amount.",
          "Balance is credited only when a payment is confirmed by our payment processor. Balance has no cash value, does not earn interest, and cannot be transferred between accounts.",
        ],
      },
      {
        heading: "4. Acceptable use",
        body: ["You agree not to use the service to:"],
        list: [
          "commit fraud, or impersonate another person or organisation;",
          "gain unauthorised access to any account that is not yours;",
          "evade a ban, suspension or other enforcement action taken against you by a third-party platform;",
          "send unsolicited bulk messages, or operate spam or bot networks;",
          "do anything unlawful where you are, or where the number's country is located;",
          "resell or automate access to the service without our written agreement.",
        ],
      },
      {
        heading: "5. Third-party platforms",
        body: [
          "Using a virtual number or a disposable inbox to register with a third-party service may breach that service's own terms. That is between you and them. We are not affiliated with, endorsed by, or connected to any of the platforms named in our catalogue — their names appear solely to identify which service a number works with.",
          "We cannot guarantee that any given platform will accept a number, and a platform's refusal is not a fault of the service.",
        ],
      },
      {
        heading: "6. Payments",
        body: [
          "Card payments are processed by Stripe and cryptocurrency payments by NOWPayments. We never see or store your full card details.",
          "Cryptocurrency transfers are irreversible. Send the exact amount to the address shown, on the network shown. Funds sent on the wrong network, or after an invoice expires, cannot be recovered by us.",
        ],
      },
      {
        heading: "7. Referral programme",
        body: [
          "You may invite others with your referral link. When someone you referred makes their qualifying first top-up, a credit is queued to your account and released after a 48-hour holding period, subject to a monthly cap. The percentage, the qualifying minimum and the cap are shown on your profile page and may change.",
          "Credits obtained through self-referral, fake accounts or any other manipulation are void, and we may close the accounts involved.",
        ],
      },
      {
        heading: "8. Availability",
        body: [
          "We aim to keep the service running but do not guarantee uninterrupted availability. Maintenance, network outages and network failures happen. We do not offer a service-level agreement.",
        ],
      },
      {
        heading: "9. Liability",
        body: [
          "To the extent permitted by law, our total liability to you for any claim is limited to the balance remaining in your account plus any amount you paid us in the three months before the claim arose.",
          "We are not liable for indirect or consequential loss, including loss of profit, loss of access to a third-party account, or loss arising from a platform refusing a number.",
          "Nothing here limits liability that cannot lawfully be limited, including for fraud, and nothing affects the statutory rights you have as a consumer.",
        ],
      },
      {
        heading: "10. Suspension and closure",
        body: [
          "We may suspend or close an account that breaches these terms. Where we close an account for a breach involving fraud or abuse, remaining balance may be forfeited. Where we close an account for any other reason, you may ask us to return the unused balance.",
          "You can stop using the service at any time and ask us to delete your account — see the Privacy Policy.",
        ],
      },
      {
        heading: "11. Changes",
        body: [
          "We may update these terms. The date at the top of this page shows the last substantive change. Continuing to use the service after a change means you accept the updated terms; if you do not, stop using the service and ask for your unused balance back.",
        ],
      },
      {
        heading: "12. Governing law and contact",
        body: lines(
          governingLawLine("en"),
          identificationLine("en"),
          `Questions about these terms: ${supportChannel("en")}.`,
        ),
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    metaTitle: "Privacy Policy — SmsEmpire",
    metaDescription:
      "What personal data SmsEmpire collects, why we collect it, who processes it on our behalf, how long we keep it, and how to exercise your rights.",
    updated: LAST_UPDATED,
    intro: `${operatorName()} is the data controller for the personal data described here. This policy explains what we collect, why, and what you can do about it.`,
    sections: [
      {
        heading: "1. What we collect",
        list: [
          "Account data: email address, username, password (stored only as a bcrypt hash — we cannot read it), and the date you registered.",
          "Verification data: the email-confirmation token we send you, and whether your address has been confirmed.",
          "Usage data: your activation history — which service you requested, which country the number came from, the price, the status, and the received code — plus your top-up history and balance.",
          "Support data: the messages and contact address in any ticket you open.",
          "Technical data: your IP address, which our web server and rate limiter process in order to keep the service available.",
          "Referral data: who invited you, and the credits earned.",
        ],
        body: [
          "We do not ask for your real name, your postal address or any identity document, and we do not receive your card number — Stripe handles that directly.",
        ],
      },
      {
        heading: "2. Why we process it, and on what legal basis",
        list: [
          "To provide the service you asked for — account, activations, balance, support. Basis: performance of a contract.",
          "To keep the service secure and prevent abuse — rate limiting, ban enforcement, referral anti-fraud. Basis: our legitimate interest in a service that is not defrauded.",
          "To comply with accounting and tax obligations relating to payments. Basis: legal obligation.",
          "To send you service email — address confirmation, and replies to your tickets. Basis: performance of a contract. We do not send marketing email.",
        ],
      },
      {
        heading: "3. Who else processes your data",
        body: ["We use these processors, and only for the purposes above:"],
        list: [
          "Our SMS and email systems — process the service and country you request in order to allocate a number or inbox.",
          "Stripe — card payments. Stripe is the controller of the card data it collects; we receive only a payment reference and the amount.",
          "NOWPayments — cryptocurrency payments, on the same basis.",
          "Our email infrastructure — delivers confirmation email; receives your address and the message.",
          "Our hosting infrastructure — operates the server on which the application and database run.",
        ],
      },
      {
        heading: "4. International transfers",
        body: [
          "Some of these processors operate outside the European Economic Area. Where that happens, transfers rely on the safeguards those providers offer, such as the European Commission's standard contractual clauses.",
        ],
      },
      {
        heading: "5. How long we keep it",
        list: [
          "Account data: while your account exists, and for 30 days after you ask us to delete it.",
          "Activation and top-up records: retained as long as required by accounting law in our country of establishment, because they are records of a paid transaction. Received SMS codes may be deleted earlier, and you can clear your own activation history from the app at any time.",
          "Support tickets: two years after the ticket is closed.",
          "Server and rate-limiting logs: short-lived, and not used to build a profile of you.",
        ],
      },
      {
        heading: "6. Your rights",
        body: [
          "You can ask us to give you a copy of your data, correct it, delete it, restrict or object to its processing, or provide it in a portable format. You can also withdraw consent where processing relies on consent.",
          `Write to us through ${privacyChannel("en")}, from the address registered on your account. We answer within one month. If you are unhappy with our answer you can complain to the data protection authority where you live.`,
          "Note that we cannot delete records we are legally required to retain for accounting purposes; in that case we restrict them instead of erasing them.",
        ],
      },
      {
        heading: "7. Security",
        body: [
          "Passwords are stored as bcrypt hashes. Sessions use signed tokens. Traffic is served over HTTPS. Payment webhooks are verified cryptographically before any balance is credited.",
          "No system is perfectly secure. If a breach affects your rights we will notify you and the relevant authority as the law requires.",
        ],
      },
      {
        heading: "8. Children",
        body: [
          "The service is not for anyone under 18 and we do not knowingly collect data from children. If you believe a child has registered, tell us and we will remove the account.",
        ],
      },
      {
        heading: "9. Changes and contact",
        body: lines(
          "We will update this policy when our processing changes; the date at the top shows the last substantive change.",
          identificationLine("en"),
          `Data protection contact: ${privacyChannel("en")}.`,
        ),
      },
    ],
  },

  refund: {
    title: "Refund Policy",
    metaTitle: "Refund Policy — SmsEmpire",
    metaDescription:
      "When an SMS activation is refunded to your SmsEmpire balance, the cancellation window, and when a top-up can be returned to your payment method.",
    updated: LAST_UPDATED,
    intro:
      "This policy explains exactly when money comes back, because a prepaid service should be unambiguous about that. Read the cancellation window in section 2 carefully — it is the part that costs people money.",
    sections: [
      {
        heading: "1. Two different things",
        body: [
          "An activation refund returns the price of a single activation to your account balance. A top-up refund returns money to the card or wallet you paid with. They follow different rules, set out below.",
        ],
      },
      {
        heading: "2. Activation refunds — the cancellation window",
        body: [
          "When you request a number, its price is deducted immediately. You get that price back in full, to your balance, if you cancel the activation before a code arrives — but only within a specific window:",
        ],
        list: [
          "You cannot cancel during the first 120 seconds. This minimum is imposed by the telecom network. Trying earlier returns an error asking you to wait.",
          "You can cancel from 120 seconds up to 20 minutes after requesting the number. Cancelling in this window refunds the full price to your balance immediately.",
          "After 20 minutes the activation expires automatically and is not refunded. If you request a number and walk away, you lose its price.",
          "Once a code has been received, the activation cannot be cancelled and is not refundable — the service you paid for was delivered.",
        ],
      },
      {
        heading: "3. Pre-made account orders",
        body: [
          "Orders for pre-made accounts are fulfilled by hand and cannot be cancelled from the app. If an order cannot be fulfilled, or the credentials delivered do not work on arrival, open a ticket within 48 hours of delivery and we will replace the account or refund its price to your balance.",
        ],
      },
      {
        heading: "4. Top-up refunds",
        body: [
          "Unused balance can be returned to your original payment method on request, less any transaction fee our processor does not return to us. Ask through a support ticket from the account's registered address.",
          "Balance that has already been spent on activations cannot be returned — that money has been consumed in the network.",
          "Cryptocurrency top-ups are refunded to a wallet address you provide, in the same currency. Network fees are deducted. Because blockchain transfers are irreversible, we cannot recover funds sent to a wrong address or on a wrong network.",
        ],
      },
      {
        heading: "5. What we cannot refund",
        list: [
          "Activations that received a code.",
          "Activations left to expire past the 20-minute window.",
          "Cases where a third-party platform rejected the number, blocked your account, or removed a registration afterwards. We sell access to a number and the delivery of a code to it; we do not sell an outcome on someone else's platform.",
          "Balance forfeited when an account is closed for fraud or abuse, as set out in the Terms of Service.",
        ],
      },
      {
        heading: "6. How to request a refund",
        body: [
          `Open a ticket from your account, or reach us through ${supportChannel("en")} from your registered address, including the activation or top-up reference. We reply within 72 hours and resolve accepted requests within 14 days.`,
          "If you are a consumer in the European Union, this policy does not affect your statutory rights, including the right of withdrawal where it applies. Note that once you request a number and a code is delivered, the service has been fully performed.",
        ],
      },
    ],
  },

  cookies: {
    title: "Cookie Policy",
    metaTitle: "Cookie Policy — SmsEmpire",
    metaDescription:
      "SmsEmpire uses no advertising or analytics cookies. This page lists exactly what the site stores in your browser and why.",
    updated: LAST_UPDATED,
    intro:
      "The short version: we set no advertising cookies, no analytics cookies and no third-party trackers. What we store is what the site needs in order to work, and it is stored in your browser rather than sent to us on every request.",
    sections: [
      {
        heading: "1. What we store, and why",
        body: [
          "The following entries are kept in your browser's local storage. All of them are strictly necessary for the feature they support, which is why no consent banner is required for them.",
        ],
        list: [
          "vh_token — your session token. Without it you would be logged out on every page load. Removed when you log out.",
          "locale — the interface language you chose, so the site remembers it.",
          "currency — the display currency you chose.",
          "theme — whether you prefer the light or dark interface.",
          "vh_ref_code — the referral code from the link you arrived through, so credit reaches the right person when you register.",
          "mlp_mode — whether you enabled the optional sound effect in your profile settings.",
        ],
      },
      {
        heading: "2. What we do not use",
        list: [
          "No advertising or retargeting cookies.",
          "No analytics or behavioural tracking.",
          "No social media pixels or embedded trackers.",
          "No cross-site profiling of any kind.",
        ],
      },
      {
        heading: "3. Payment providers",
        body: [
          "When you open the card payment form, Stripe loads its own script and may set cookies necessary to process the payment and prevent fraud. This is Stripe acting as its own controller; see Stripe's privacy notice for detail. The same applies to NOWPayments when you pay with cryptocurrency.",
        ],
      },
      {
        heading: "4. Controlling this",
        body: [
          "You can clear this storage from your browser settings at any time. Doing so logs you out and resets your language, currency and theme preferences to their defaults. The service will continue to work.",
        ],
      },
      {
        heading: "5. If this changes",
        body: [
          "If we ever add analytics, we will ask for your consent first, and this page will be updated to list exactly what is set and by whom.",
        ],
      },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Spanish
// ─────────────────────────────────────────────────────────────────────────────

const ES: Record<LegalSlug, LegalDoc> = {
  terms: {
    title: "Términos del servicio",
    metaTitle: "Términos del servicio — SmsEmpire",
    metaDescription:
      "Condiciones de uso de SmsEmpire: qué hace el servicio, para qué puedes y no puedes usarlo, cómo funciona el saldo prepago y cómo termina el acuerdo.",
    updated: LAST_UPDATED,
    intro: `Estos términos constituyen el acuerdo entre tú y ${operatorName()} («nosotros»)${OPERATOR.legalName ? `, operador de ${OPERATOR.tradingName}` : ""}. Al crear una cuenta los aceptas. Si no los aceptas, no uses el servicio.`,
    sections: [
      {
        heading: "1. Qué es el servicio",
        body: [
          `${OPERATOR.tradingName} es un proveedor directo. Proporcionamos acceso a números de teléfono virtuales y buzones de correo desechables, y revendemos cuentas ya creadas. No somos titulares de los rangos de numeración ni operamos las redes móviles que entregan los mensajes.`,
          "Esto importa para lo que podemos prometer: podemos entregarte un mensaje que nos llegue, pero no podemos obligar a un tercero a enviarlo. La disponibilidad, los tiempos de entrega y el stock por país los determina la capacidad de nuestra red y la plataforma en la que te estés verificando.",
        ],
      },
      {
        heading: "2. Quién puede usarlo",
        body: [
          "Debes tener al menos 18 años y capacidad legal para contratar. Cada persona puede tener una cuenta. Eres responsable de todo lo que se haga desde ella, así que no compartas tu contraseña.",
          "Podemos rechazar o cerrar una cuenta cuando tengamos motivos razonables para creer que se están incumpliendo estos términos.",
        ],
      },
      {
        heading: "3. Saldo prepago",
        body: [
          "El servicio funciona con saldo prepago. Cargas fondos y cada activación descuenta su precio en el momento en que la solicitas. Los precios se expresan en dólares estadounidenses; cualquier otra divisa que veas en la interfaz es una conversión orientativa, y el importe que cobra la pasarela de pago es el importe en dólares.",
          "El saldo se acredita únicamente cuando la pasarela confirma el pago. El saldo no tiene valor en efectivo, no genera intereses y no se puede transferir entre cuentas.",
        ],
      },
      {
        heading: "4. Uso aceptable",
        body: ["Te comprometes a no usar el servicio para:"],
        list: [
          "cometer fraude ni suplantar a otra persona u organización;",
          "acceder sin autorización a cuentas que no sean tuyas;",
          "eludir un bloqueo, suspensión o sanción que una plataforma externa te haya impuesto;",
          "enviar mensajes masivos no solicitados ni operar redes de spam o bots;",
          "cualquier actividad ilícita en tu país o en el país del número;",
          "revender o automatizar el acceso al servicio sin acuerdo escrito con nosotros.",
        ],
      },
      {
        heading: "5. Plataformas de terceros",
        body: [
          "Usar un número virtual o un buzón desechable para registrarte en un servicio externo puede incumplir las condiciones de ese servicio. Eso es un asunto entre esa plataforma y tú. No estamos afiliados, avalados ni relacionados con ninguna de las plataformas que aparecen en nuestro catálogo: sus nombres figuran solo para identificar con qué servicio funciona cada número.",
          "No podemos garantizar que una plataforma concreta acepte un número, y que lo rechace no constituye un defecto del servicio.",
        ],
      },
      {
        heading: "6. Pagos",
        body: [
          "Los pagos con tarjeta los procesa Stripe y los pagos con criptomoneda NOWPayments. En ningún momento vemos ni almacenamos los datos completos de tu tarjeta.",
          "Las transferencias de criptomoneda son irreversibles. Envía el importe exacto a la dirección indicada y por la red indicada. No podemos recuperar fondos enviados por una red equivocada o después de que caduque la factura.",
        ],
      },
      {
        heading: "7. Programa de referidos",
        body: [
          "Puedes invitar a otras personas con tu enlace de referido. Cuando alguien a quien invitaste hace su primera recarga válida, se encola un crédito a tu favor que se libera tras un periodo de retención de 48 horas y está sujeto a un tope mensual. El porcentaje, el mínimo exigido y el tope se muestran en tu perfil y pueden cambiar.",
          "Los créditos obtenidos mediante autorreferencia, cuentas falsas o cualquier otra manipulación quedan anulados, y podemos cerrar las cuentas implicadas.",
        ],
      },
      {
        heading: "8. Disponibilidad",
        body: [
          "Procuramos mantener el servicio en funcionamiento, pero no garantizamos disponibilidad ininterrumpida. Hay mantenimientos, caídas de red y fallos de red. No ofrecemos acuerdo de nivel de servicio.",
        ],
      },
      {
        heading: "9. Responsabilidad",
        body: [
          "En la medida en que lo permita la ley, nuestra responsabilidad total frente a ti por cualquier reclamación se limita al saldo que te quede en la cuenta más lo que nos hayas pagado en los tres meses anteriores a la reclamación.",
          "No respondemos por daños indirectos o consecuenciales, incluidos el lucro cesante, la pérdida de acceso a una cuenta de terceros o los perjuicios derivados de que una plataforma rechace un número.",
          "Nada de lo anterior limita responsabilidades que la ley no permite limitar, incluido el dolo, ni afecta a tus derechos como consumidor.",
        ],
      },
      {
        heading: "10. Suspensión y cierre",
        body: [
          "Podemos suspender o cerrar una cuenta que incumpla estos términos. Si la cerramos por un incumplimiento que implique fraude o abuso, el saldo restante puede perderse. Si la cerramos por cualquier otro motivo, puedes pedirnos la devolución del saldo no consumido.",
          "Puedes dejar de usar el servicio cuando quieras y pedirnos que eliminemos tu cuenta: consulta la Política de privacidad.",
        ],
      },
      {
        heading: "11. Cambios",
        body: [
          "Podemos actualizar estos términos. La fecha que figura arriba indica el último cambio sustancial. Seguir usando el servicio después de un cambio implica que aceptas la versión actualizada; si no la aceptas, deja de usarlo y pide la devolución del saldo no consumido.",
        ],
      },
      {
        heading: "12. Ley aplicable y contacto",
        body: lines(
          governingLawLine("es"),
          identificationLine("es"),
          `Dudas sobre estos términos: ${supportChannel("es")}.`,
        ),
      },
    ],
  },

  privacy: {
    title: "Política de privacidad",
    metaTitle: "Política de privacidad — SmsEmpire",
    metaDescription:
      "Qué datos personales recoge SmsEmpire, para qué, quién los trata por nuestra cuenta, cuánto tiempo los conservamos y cómo ejercer tus derechos.",
    updated: LAST_UPDATED,
    intro: `${operatorName()} es el responsable del tratamiento de los datos que se describen aquí. Esta política explica qué recogemos, para qué, y qué puedes hacer al respecto.`,
    sections: [
      {
        heading: "1. Qué recogemos",
        list: [
          "Datos de cuenta: dirección de correo, nombre de usuario, contraseña (almacenada solo como hash bcrypt, no podemos leerla) y fecha de registro.",
          "Datos de verificación: el token de confirmación que te enviamos por correo y si tu dirección está confirmada.",
          "Datos de uso: tu historial de activaciones —qué servicio pediste, de qué país salió el número, el precio, el estado y el código recibido— además de tu historial de recargas y tu saldo.",
          "Datos de soporte: los mensajes y la dirección de contacto de los tickets que abras.",
          "Datos técnicos: tu dirección IP, que tratan el servidor web y el limitador de peticiones para mantener el servicio disponible.",
          "Datos de referidos: quién te invitó y los créditos generados.",
        ],
        body: [
          "No te pedimos nombre real, dirección postal ni documento de identidad, y no recibimos el número de tu tarjeta: de eso se encarga Stripe directamente.",
        ],
      },
      {
        heading: "2. Para qué los tratamos y con qué base legal",
        list: [
          "Para prestarte el servicio que has contratado: cuenta, activaciones, saldo y soporte. Base: ejecución de un contrato.",
          "Para mantener el servicio seguro y prevenir abusos: límites de peticiones, aplicación de bloqueos y antifraude de referidos. Base: interés legítimo en que el servicio no sea defraudado.",
          "Para cumplir obligaciones contables y fiscales relativas a los pagos. Base: obligación legal.",
          "Para enviarte correo de servicio: confirmación de dirección y respuestas a tus tickets. Base: ejecución de un contrato. No enviamos correo comercial.",
        ],
      },
      {
        heading: "3. Quién más trata tus datos",
        body: ["Utilizamos estos encargados, y solo para los fines anteriores:"],
        list: [
          "Nuestros sistemas de SMS y correo: procesan el servicio y el país que solicitas para asignar un número o un buzón.",
          "Stripe: pagos con tarjeta. Stripe es responsable de los datos de tarjeta que recoge; nosotros solo recibimos una referencia de pago y el importe.",
          "NOWPayments: pagos con criptomoneda, en los mismos términos.",
          "Nuestra infraestructura de correo: entrega los mensajes de confirmación; recibe tu dirección y el contenido.",
          "Nuestra infraestructura de alojamiento: opera el servidor donde se ejecutan la aplicación y la base de datos.",
        ],
      },
      {
        heading: "4. Transferencias internacionales",
        body: [
          "Algunos de estos encargados operan fuera del Espacio Económico Europeo. Cuando eso ocurre, las transferencias se amparan en las garantías que ofrecen dichas redes, como las cláusulas contractuales tipo de la Comisión Europea.",
        ],
      },
      {
        heading: "5. Cuánto tiempo los conservamos",
        list: [
          "Datos de cuenta: mientras exista tu cuenta y 30 días después de que pidas su eliminación.",
          "Registros de activaciones y recargas: el plazo que exija la normativa contable de nuestro país de establecimiento, por tratarse de justificantes de operaciones con pago. Los códigos SMS recibidos pueden borrarse antes, y puedes vaciar tu propio historial desde la aplicación cuando quieras.",
          "Tickets de soporte: dos años desde el cierre del ticket.",
          "Registros del servidor y del limitador de peticiones: de vida breve, y no se usan para elaborar un perfil sobre ti.",
        ],
      },
      {
        heading: "6. Tus derechos",
        body: [
          "Puedes pedirnos una copia de tus datos, su rectificación o supresión, la limitación u oposición al tratamiento, y su portabilidad. También puedes retirar el consentimiento cuando el tratamiento se base en él.",
          `Escríbenos a través de ${privacyChannel("es")}, desde la dirección registrada en tu cuenta. Respondemos en el plazo de un mes. Si nuestra respuesta no te satisface, puedes reclamar ante la autoridad de protección de datos de tu país.`,
          "Ten en cuenta que no podemos suprimir registros que la ley nos obliga a conservar por motivos contables; en ese caso los limitamos en lugar de borrarlos.",
        ],
      },
      {
        heading: "7. Seguridad",
        body: [
          "Las contraseñas se almacenan como hashes bcrypt. Las sesiones usan tokens firmados. El tráfico se sirve por HTTPS. Los webhooks de pago se verifican criptográficamente antes de acreditar cualquier saldo.",
          "Ningún sistema es perfectamente seguro. Si una brecha afecta a tus derechos, te lo notificaremos a ti y a la autoridad competente conforme exige la ley.",
        ],
      },
      {
        heading: "8. Menores",
        body: [
          "El servicio no está dirigido a menores de 18 años y no recogemos datos de menores de forma consciente. Si crees que un menor se ha registrado, avísanos y eliminaremos la cuenta.",
        ],
      },
      {
        heading: "9. Cambios y contacto",
        body: lines(
          "Actualizaremos esta política cuando cambien nuestros tratamientos; la fecha de arriba indica el último cambio sustancial.",
          identificationLine("es"),
          `Contacto de protección de datos: ${privacyChannel("es")}.`,
        ),
      },
    ],
  },

  refund: {
    title: "Política de reembolsos",
    metaTitle: "Política de reembolsos — SmsEmpire",
    metaDescription:
      "Cuándo se reembolsa una activación SMS a tu saldo de SmsEmpire, cuál es la ventana de cancelación y cuándo se puede devolver una recarga a tu medio de pago.",
    updated: LAST_UPDATED,
    intro:
      "Esta política explica exactamente cuándo se devuelve el dinero, porque un servicio de prepago debe ser inequívoco en eso. Lee con atención la ventana de cancelación del apartado 2: es la parte que hace perder dinero a la gente.",
    sections: [
      {
        heading: "1. Dos cosas distintas",
        body: [
          "El reembolso de una activación devuelve el precio de esa activación a tu saldo. El reembolso de una recarga devuelve dinero a la tarjeta o monedero con el que pagaste. Siguen reglas diferentes, que se detallan a continuación.",
        ],
      },
      {
        heading: "2. Reembolso de activaciones: la ventana de cancelación",
        body: [
          "Cuando solicitas un número, su precio se descuenta al instante. Recuperas ese precio íntegro, en tu saldo, si cancelas la activación antes de que llegue un código, pero solo dentro de una ventana concreta:",
        ],
        list: [
          "No puedes cancelar durante los primeros 120 segundos. Ese mínimo lo impone la red de telecomunicaciones. Si lo intentas antes, verás un error pidiéndote que esperes.",
          "Puedes cancelar desde los 120 segundos y hasta los 20 minutos posteriores a la solicitud. Cancelar dentro de esa ventana reembolsa el precio completo a tu saldo de forma inmediata.",
          "Pasados los 20 minutos la activación caduca automáticamente y no se reembolsa. Si pides un número y te vas, pierdes su precio.",
          "Una vez recibido el código, la activación no se puede cancelar ni reembolsar: el servicio que pagaste se ha prestado.",
        ],
      },
      {
        heading: "3. Pedidos de cuentas ya creadas",
        body: [
          "Los pedidos de cuentas se atienden manualmente y no se pueden cancelar desde la aplicación. Si un pedido no puede completarse, o las credenciales entregadas no funcionan de inicio, abre un ticket en las 48 horas siguientes a la entrega y sustituiremos la cuenta o reembolsaremos su precio a tu saldo.",
        ],
      },
      {
        heading: "4. Reembolso de recargas",
        body: [
          "El saldo no consumido puede devolverse a tu medio de pago original si lo solicitas, descontando la comisión que la pasarela no nos reintegre. Pídelo mediante un ticket desde la dirección registrada en la cuenta.",
          "El saldo ya gastado en activaciones no se puede devolver: ese dinero se ha consumido en la red.",
          "Las recargas con criptomoneda se reembolsan a una dirección que nos indiques, en la misma moneda, descontando las comisiones de red. Al ser las transferencias irreversibles, no podemos recuperar fondos enviados a una dirección o una red equivocadas.",
        ],
      },
      {
        heading: "5. Qué no podemos reembolsar",
        list: [
          "Activaciones que recibieron código.",
          "Activaciones que se dejaron caducar pasada la ventana de 20 minutos.",
          "Casos en los que una plataforma externa rechazó el número, bloqueó tu cuenta o eliminó el registro después. Vendemos acceso a un número y la entrega de un código a ese número; no vendemos un resultado en la plataforma de un tercero.",
          "El saldo perdido cuando se cierra una cuenta por fraude o abuso, según se establece en los Términos del servicio.",
        ],
      },
      {
        heading: "6. Cómo solicitar un reembolso",
        body: [
          `Abre un ticket desde tu cuenta o contáctanos mediante ${supportChannel("es")}, desde tu dirección registrada, indicando la referencia de la activación o de la recarga. Respondemos en 72 horas y resolvemos las solicitudes aceptadas en un plazo de 14 días.`,
          "Si eres consumidor en la Unión Europea, esta política no afecta a tus derechos legales, incluido el derecho de desistimiento cuando proceda. Ten en cuenta que, una vez solicitado un número y entregado un código, el servicio se ha ejecutado por completo.",
        ],
      },
    ],
  },

  cookies: {
    title: "Política de cookies",
    metaTitle: "Política de cookies — SmsEmpire",
    metaDescription:
      "SmsEmpire no usa cookies publicitarias ni de analítica. Esta página detalla exactamente qué guarda el sitio en tu navegador y para qué.",
    updated: LAST_UPDATED,
    intro:
      "La versión corta: no ponemos cookies publicitarias, ni de analítica, ni rastreadores de terceros. Lo que guardamos es lo que el sitio necesita para funcionar, y se queda en tu navegador en lugar de enviarse en cada petición.",
    sections: [
      {
        heading: "1. Qué guardamos y para qué",
        body: [
          "Las siguientes entradas se guardan en el almacenamiento local de tu navegador. Todas son estrictamente necesarias para la función que sostienen, y por eso no requieren banner de consentimiento.",
        ],
        list: [
          "vh_token: el token de tu sesión. Sin él se cerraría tu sesión en cada carga de página. Se elimina al cerrar sesión.",
          "locale: el idioma de interfaz que elegiste, para que el sitio lo recuerde.",
          "currency: la divisa de visualización que elegiste.",
          "theme: si prefieres la interfaz clara u oscura.",
          "vh_ref_code: el código de referido del enlace por el que llegaste, para que el crédito llegue a quien corresponde cuando te registres.",
          "mlp_mode: si activaste el efecto de sonido opcional en los ajustes de tu perfil.",
        ],
      },
      {
        heading: "2. Qué no usamos",
        list: [
          "Ninguna cookie publicitaria ni de retargeting.",
          "Ninguna analítica ni seguimiento de comportamiento.",
          "Ningún píxel de redes sociales ni rastreador incrustado.",
          "Ningún perfilado entre sitios de ningún tipo.",
        ],
      },
      {
        heading: "3. Pasarelas de pago",
        body: [
          "Al abrir el formulario de pago con tarjeta, Stripe carga su propio script y puede establecer cookies necesarias para procesar el pago y prevenir el fraude. Ahí Stripe actúa como responsable propio; consulta su aviso de privacidad para el detalle. Lo mismo aplica a NOWPayments cuando pagas con criptomoneda.",
        ],
      },
      {
        heading: "4. Cómo controlarlo",
        body: [
          "Puedes borrar este almacenamiento desde los ajustes de tu navegador cuando quieras. Al hacerlo se cerrará tu sesión y se restablecerán el idioma, la divisa y el tema a sus valores por defecto. El servicio seguirá funcionando.",
        ],
      },
      {
        heading: "5. Si esto cambia",
        body: [
          "Si algún día añadimos analítica, te pediremos consentimiento antes, y esta página se actualizará para detallar exactamente qué se establece y quién lo hace.",
        ],
      },
    ],
  },
};

export const LEGAL: Record<LegalLang, Record<LegalSlug, LegalDoc>> = { en: EN, es: ES };

export function getLegalDoc(slug: LegalSlug, locale: string): LegalDoc {
  return LEGAL[resolveLegalLang(locale)][slug];
}
